﻿import {isPromiseAlike, nanoid} from "@2d-front-monorepo/core";
import React, {FC, JSX, memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {TabPanelProps} from "./TabPanelProps";
import {TabsProps} from "./TabsProps";

interface TabButtonProps {
	id: string;
	panelId: string
	title: string;
	selected: boolean;
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

function TabButton({title, id, panelId, selected, onClick}: TabButtonProps): JSX.Element {
	return <li role="presentation">
		<button id={id}
		        aria-controls={panelId}
		        type="button"
		        role="tab"
		        aria-selected={selected ? true : undefined}
		        tabIndex={selected ? 0 : -1}
		        onClick={onClick}>
			{title}
		</button>
	</li>;
}

function Tabs(props: TabsProps): JSX.Element {
	const tabPanels = React.Children.toArray(props.children) as ReactElement<TabPanelProps>[];
	const tabIds = useMemo(() => tabPanels.map(() => "tab_" + nanoid()), [tabPanels.length]);
	const panelIds = useMemo(() => tabPanels.map((_, i) => "panel_" + tabIds[i]), [tabIds]);
	const firstVisible = useMemo(() => tabPanels.findIndex(p => p.props.hidden !== true), [tabPanels]);
	const initialSelected = props.selected != null && tabPanels[props.selected]?.props.hidden !== true
		? props.selected
		: Math.max(firstVisible, 0);
	const [selected, setSelected] = useState(initialSelected);
	// A vertical tablist is a column that grows the container instead of overflowing it, so none
	// of the horizontal drag-scroll machinery below (nor the .tabs-scroll shadow wrapper) applies.
	const vertical = props.vertical === true;
	const klass = useMemo(() => "tabs " + (vertical ? "tabs--block" : "tabs--inline")
		+ (props.className ? " " + props.className : ""), [props.className, vertical]);

	// Click+hold+drag to scroll the tablist (native touch scroll already covers mobile).
	const listRef = useRef<HTMLUListElement>(null);
	const drag = useRef({down: false, startX: 0, startLeft: 0, moved: false});
	const [edges, setEdges] = useState({left: false, right: false});

	const updateEdges = useCallback(() => {
		const el = listRef.current;
		if (el == null || vertical) return;
		const left = el.scrollLeft > 0;
		const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
		setEdges(prev => (prev.left === left && prev.right === right ? prev : {left, right}));
	}, [vertical]);

	// Recompute once laid out and whenever the tab set changes.
	useEffect(updateEdges, [updateEdges, tabPanels.length, selected]);

	const onPointerDown = useCallback((e: React.PointerEvent<HTMLUListElement>) => {
		const el = listRef.current;
		if (el == null) return;
		// Don't capture yet: capturing here would retarget the click off the tab button.
		drag.current = {down: true, startX: e.clientX, startLeft: el.scrollLeft, moved: false};
	}, []);

	const onPointerMove = useCallback((e: React.PointerEvent<HTMLUListElement>) => {
		const el = listRef.current;
		const d = drag.current;
		if (el == null || !d.down) return;
		const dx = e.clientX - d.startX;
		if (!d.moved && Math.abs(dx) > 3) {
			d.moved = true;
			el.setPointerCapture(e.pointerId); // now it's a real drag — keep tracking outside the list
		}
		if (d.moved) el.scrollLeft = d.startLeft - dx;
	}, []);

	const onPointerUp = useCallback(() => {
		drag.current.down = false;
	}, []);

	const onClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		if (drag.current.moved) return; // it was a drag-scroll, not a tab selection
		const next = tabIds.indexOf(e.currentTarget.id);
		if (next === selected) return;

		const onChange = props.onChange;
		if (onChange == null) {
			setSelected(next);
			return;
		}

		const result = onChange(next, selected);
		if (isPromiseAlike<void | boolean>(result)) {
			result.then(allowed => {
				if (allowed !== false) setSelected(next);
			});
		} else if (result !== false) {
			setSelected(next);
		}
	}, [tabIds, selected, props.onChange]);

	// Vertical never scrolls, so it gets none of these handlers.
	const scrollProps: React.HTMLAttributes<HTMLUListElement> = vertical ? {} : {
		onScroll: updateEdges,
		onPointerDown: onPointerDown,
		onPointerMove: onPointerMove,
		onPointerUp: onPointerUp,
		onPointerCancel: onPointerUp
	};

	const tabList = <ul role="tablist" ref={listRef} {...scrollProps}>
		{tabPanels.map(function (tabPanel, index) {
			if (tabPanel.props.hidden === true) return null;
			return <TabButton title={tabPanel.props.title} id={tabIds[index]} panelId={panelIds[index]}
			                  key={index} selected={index === selected} onClick={onClick}/>;
		})}
	</ul>;

	return <div className={klass}>
		{vertical
			? tabList
			: <div className={"tabs-scroll"
				+ (edges.left ? " can-scroll-left" : "")
				+ (edges.right ? " can-scroll-right" : "")}>
				{tabList}
			</div>}

		<div role="tabpanel" aria-labelledby={tabIds[selected]} id={panelIds[selected]}>
			{tabPanels[selected]}
		</div>
	</div>;
}

const MemoizedTabs: FC<TabsProps> = memo(Tabs);
export default MemoizedTabs;
